import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import { MoreInfo } from '@/sections/referral/MoreInfo';
import { Faq } from '@/sections/referral/Faq';
import { FaqList } from '@/sections/referral/FaqList';
import { HowToRecommend } from '@/sections/referral/HowToRecommend';
import { HowItWorks } from '@/sections/referral/HowItWorks';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { SectionHeadline } from '@/components/SectionHeadline';
import { DownloadButton } from '@/components/buttons';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import { SELF_CARE_LINKS } from '@/constants/links';
import React from 'react';
import { FILES } from '@/constants/files';
import { useRouter } from 'next/router';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(58);
  usePageView("referral");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["referral", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    locale
  } = useRouter();
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  let t1;
  if ($[1] !== t) {
    t1 = t("seo.title");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("seo.description");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <SeoHeader title={t1} description={t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = pageBanner?.videoId;
  let t5;
  if ($[8] !== pageBanner?.title || $[9] !== t) {
    t5 = pageBanner?.title ?? t("banner.title");
    $[8] = pageBanner?.title;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== pageBanner?.text || $[12] !== t) {
    t6 = pageBanner?.text ?? t("banner.text");
    $[11] = pageBanner?.text;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== pageBanner || $[15] !== t) {
    t7 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: SELF_CARE_LINKS.referral,
      testId: "referralsHeroPrimaryCTA",
      analyticsParam: "referralsHeroPrimaryCTA",
      variant: "primary"
    }];
    $[14] = pageBanner;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = pageBanner?.image?.url ?? "/images/sections/referral/hero/hero.png";
  let t9;
  if ($[17] !== pageBanner?.image?.alt || $[18] !== t) {
    t9 = pageBanner?.image?.alt ?? t("banner.title");
    $[17] = pageBanner?.image?.alt;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = {
      url: t8,
      alt: t9
    };
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = pageBanner?.variant || "mascots";
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t12 = <Hero videoId={t4} title={t5} text={t6} buttons={t7} image={t10} variant={t11} />;
    $[23] = t10;
    $[24] = t11;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="absolute top-1/3 right-0 h-64 w-64 -translate-y-3/4 overflow-hidden lg:h-[37.5rem] lg:w-[37.5rem]"><div className="bgCircle absolute top-0 right-0 h-64 w-64 translate-x-1/4 -rotate-90 lg:h-[37.5rem] lg:w-[37.5rem]" /></div>;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="relative z-30 bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40">{t14}<div className="relative z-10 flex flex-col gap-20 lg:gap-40"><HowToRecommend /><HowItWorks /></div></div>;
    t16 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    $[32] = t15;
    $[33] = t16;
  } else {
    t15 = $[32];
    t16 = $[33];
  }
  let t17;
  let t18;
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="bgCircle absolute top-0 left-0 -translate-x-1/2 -rotate-90 lg:left-1/3 lg:h-[25rem] lg:w-[25rem] lg:-translate-x-3/4" />;
    t18 = <div className="bgCircle absolute right-0 bottom-0 hidden translate-x-1/2 translate-y-1/3 rotate-180 lg:block lg:h-[25rem] lg:w-[25rem]" />;
    t19 = <FaqList />;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t17 = $[34];
    t18 = $[35];
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t) {
    t20 = t("moreInfo.title");
    $[37] = t;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t20) {
    t21 = <SectionHeadline className="mb-10 lg:mb-16" anchorKey="moreInfo.title">{t20}</SectionHeadline>;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== locale) {
    t22 = FILES(locale);
    $[41] = locale;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t23 = t22.termsAndConditions;
  let t24;
  if ($[43] !== t) {
    t24 = t("moreInfo.file");
    $[43] = t;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t22.termsAndConditions || $[46] !== t24) {
    t25 = <div className="text-center"><DownloadButton to={t23} testId="referraTermAndConditionsFile" analyticsParam="referraTermAndConditionsFile">{t24}</DownloadButton></div>;
    $[45] = t22.termsAndConditions;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t21 || $[49] !== t25) {
    t26 = <div className="relative overflow-x-hidden bg-blue-50 pt-10 pb-20 lg:pt-20 lg:pb-40"><div className="relative z-10 flex flex-col gap-20 px-4 lg:gap-40">{t17}{t18}{t19}<div>{t21}{t25}</div></div></div>;
    $[48] = t21;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="relative overflow-hidden bg-blue-100 py-10 lg:py-20"><div className="relative z-10 flex flex-col gap-20 lg:gap-40"><Faq /><MoreInfo /></div><div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" /></div>;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== menuPromoBanners || $[54] !== t12 || $[55] !== t26 || $[56] !== t3) {
    t29 = <Layout menuPromoBanners={menuPromoBanners}>{t3}{t12}{t13}{t15}{t16}{t26}{t27}{t28}</Layout>;
    $[53] = menuPromoBanners;
    $[54] = t12;
    $[55] = t26;
    $[56] = t3;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  return t29;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('doporucte-nas');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('referral'),
      ...(await getIntlProps(ctx, ['core', 'referral']))
    }
  };
}) satisfies GetStaticProps<Props>;